import React from 'react';

/**
 * THE active-filter chip row. One removable chip per active filter, plus
 * "Clear all". Rendered directly under a `components/ui/Toolbar`, and ONLY when
 * at least one chip is active — an empty row would reserve vertical space on
 * every page that has no filters applied, which is the default state.
 *
 * Generalised out of `components/gags/GagFilterChips.js` on 2026-08-08, which
 * is DELETED. Nothing about the shell was gag-specific; the one gag thing about
 * it was that it imported `describeActiveFilters` from `constants/gagBreakdown`
 * and so could only ever describe the breakdown's filter model. The chip LIST
 * is now a prop, so each surface keeps its own describe-function next to the
 * filter model it describes. All four, and this list is the whole set:
 *   - `describeActiveFilters`  — `constants/gagBreakdown.js`      (Breakdown)
 *   - `describeOrderFilters`   — `components/OrderFilterBar.js`   (all 3 order pages)
 *   - `describeMyTaskFilters`  — `pages/MyTeamTasks.js`           (My Tasks)
 *   - `describeTeamFilters`    — `pages/TeamManage.js`            (Manage Teams)
 * The last two arrived on 2026-08-08, replacing inline hand-copies of this
 * component's markup; there are no inline copies left. 🔑 **Never pull a
 * surface's filter vocabulary in here.** The chip LIST is a prop — that is the
 * whole design, and it is what stops this file learning about gag statuses,
 * order statuses, task statuses and team types at once.
 *
 * 🔑 WHY A CHIP ROW EXISTS AT ALL — do not delete it as decoration. It is the
 * mitigation for the one real cost of putting filters behind a `Filters ▾`
 * disclosure: the page stops showing its own filter state, so a list that has
 * been narrowed looks identical to a list that is genuinely short. The chips
 * put that state back on the page. Any surface that moves controls into a
 * popover should gain a chip row in the same change.
 *
 * ⚠️ **`✕` must always RELAX the filter, never tighten it.** Every chip's
 * remove action moves the filter model toward its default. That is a
 * cross-surface contract, and it is the reason the order pages chip their
 * EXCLUDED statuses ("Hidden: Cancelled") rather than their included ones —
 * their status multi-select defaults to all-on, so a chip per *included*
 * status would give a ✕ that narrows the list. See `describeOrderFilters`.
 *
 * 🧭 WHICH WAY ROUND IS EACH SURFACE — work this out from the DEFAULT before
 * writing a describer, because both shapes exist here and they look identical
 * on screen:
 *   - default ALL-ON  → chip the EXCLUSIONS  → order pages only
 *   - default ALL-OFF → chip the SELECTIONS  → Breakdown, My Tasks, Manage Teams
 * Three of the four surfaces are the second kind, so the order pages are the
 * exception and the tempting generalisation is the wrong one.
 *
 * Search is deliberately NOT a chip on any surface — it has its own always-
 * visible input on the toolbar, so it is never hidden state.
 *
 * Props:
 *  - chips: [{ key, label, colorClass? }] — `colorClass` is a full badge class
 *    pair (light + dark); omit it for the neutral chip.
 *  - onRemove(chip): reset the one field this chip describes
 *  - onClearAll(): reset every filter to its default
 *  - label: the leading caption ("Filters")
 *  - className: spacing/layout for the row. A SEPARATE prop rather than
 *    something appended, for the same reason `Toolbar` has `marginClassName` —
 *    two conflicting Tailwind utilities on one element resolve by stylesheet
 *    order, not by the order you wrote them.
 *  - testId: optional `data-testid` for the row
 */
export default function FilterChips({
  chips,
  onRemove,
  onClearAll,
  label = 'Filters',
  className = 'mb-3',
  testId,
}) {
  if (!chips || chips.length === 0) return null;

  return (
    <div
      className={`flex flex-wrap items-center gap-1.5 ${className}`.trim()}
      aria-label="Active filters"
      data-testid={testId}
    >
      <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mr-0.5">
        {label}
      </span>
      {chips.map((chip) => (
        <span
          key={chip.key}
          className={`inline-flex items-center gap-1 pl-2.5 pr-1 py-1 text-xs font-medium rounded-full border ${
            chip.colorClass
              ? `${chip.colorClass} border-transparent`
              : 'bg-black/5 text-gray-700 border-gray-200 dark:bg-white/10 dark:text-gray-200 dark:border-slate-600'
          }`}
        >
          {chip.label}
          <button
            type="button"
            onClick={() => onRemove(chip)}
            aria-label={`Remove filter: ${chip.label}`}
            className="inline-flex items-center justify-center w-4 h-4 rounded-full font-bold leading-none hover:bg-black/10 dark:hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            ✕
          </button>
        </span>
      ))}
      <button
        type="button"
        onClick={onClearAll}
        className="ml-1 px-2 py-1 text-xs font-semibold rounded-full text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
      >
        Clear all
      </button>
    </div>
  );
}
